import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  FileText,
  RefreshCw,
  Trash2,
  Plus,
  Loader2,
  Upload,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIE = [
  "default",
  "contratti",
  "privacy",
  "sanitario",
  "amministrativo",
  "altro",
];

export default function ModulisticaAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nome, setNome] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [categoria, setCategoria] = useState("default");
  const [file, setFile] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/modulistica`, { headers: authHeaders });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Errore caricamento");
      }
      const json = await res.json();
      setItems(json.data || []);
    } catch (e) {
      toast.error(e.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setNome("");
    setDescrizione("");
    setCategoria("default");
    setFile(null);
    setDialogOpen(true);
  };

  const handleUpload = async () => {
    if (!nome.trim()) {
      toast.error("Inserisci il nome");
      return;
    }
    if (!file) {
      toast.error("Seleziona un file PDF");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("nome", nome.trim());
      fd.append("descrizione", descrizione.trim());
      fd.append("categoria", categoria);
      fd.append("file", file);
      const res = await fetch(`${API}/api/admin/modulistica`, {
        method: "POST",
        headers: authHeaders,
        body: fd,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Upload fallito");
      toast.success("Documento caricato");
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${API}/api/admin/modulistica/${id}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Eliminazione fallita");
      toast.success("Documento eliminato");
      await load();
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Modulistica / Biblioteca Affidya
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Documenti PDF disponibili nella Biblioteca Affidya degli utenti.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
          <Button size="sm" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" />
            Carica PDF
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((doc) => (
          <Card key={doc.id}>
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium">{doc.nome}</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  {doc.descrizione || doc.file_name || "—"}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="outline" className="text-[10px]">
                    {doc.categoria || "default"}
                  </Badge>
                  {doc.dimensione && (
                    <span className="text-xs text-slate-400">{doc.dimensione}</span>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:bg-red-50"
                disabled={deletingId === doc.id}
                onClick={() => handleDelete(doc.id)}
              >
                {deletingId === doc.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
        {!loading && items.length === 0 && (
          <Card>
            <CardContent className="p-10 text-center text-slate-500">
              Nessun documento caricato
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Carica documento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Nome</Label>
              <Input value={nome} onChange={(e) => setNome(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Descrizione</Label>
              <Input value={descrizione} onChange={(e) => setDescrizione(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIE.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>File PDF</Label>
              <Input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpload} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Carica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
